'use client'

import React from 'react'
import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { PrimitiveIcon } from '@gluestack-ui/core/icon/creator'
import { withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { createSelect } from '@gluestack-ui/core/select/creator'
import { cssInterop } from 'nativewind'
import {
    Actionsheet,
    ActionsheetContent,
    ActionsheetItem,
    ActionsheetItemText,
    ActionsheetDragIndicator,
    ActionsheetDragIndicatorWrapper,
    ActionsheetBackdrop,
    ActionsheetScrollView,
    ActionsheetVirtualizedList,
    ActionsheetFlatList,
    ActionsheetSectionList,
    ActionsheetSectionHeaderText,
} from './select-actionsheet'
import { Pressable, View, TextInput } from 'react-native'

import { UIIcon } from '../icon/UIIcon'

const SelectTriggerWrapper = React.forwardRef<
    React.ComponentRef<typeof Pressable>,
    React.ComponentProps<typeof Pressable>
>(function SelectTriggerWrapper({ ...props }, ref) {
    return <Pressable {...props} ref={ref} />
})

// Structural scaffolding only. All presentation (bg, border, state colours,
// text, size) lives in `components/select` — the single source of truth, matching
// the Figma Dropdown states. The gluestack interaction wiring (data-[hover|focus|
// invalid|disabled] on the trigger) is preserved; no colours are baked here.
const selectIconStyle = tva({
    base: 'fill-none',
    parentVariants: {
        size: { '2xs': '', 'xs': '', 'sm': '', 'md': '', 'lg': '', 'xl': '' },
    },
})

const selectStyle = tva({
    base: '',
})

const selectTriggerStyle = tva({
    base: 'flex-row items-center overflow-hidden',
    variants: {
        size: { xl: '', lg: '', md: '', sm: '' },
        variant: { underlined: '', outline: '', rounded: '' },
    },
})

const selectInputStyle = tva({
    base: 'web:w-full h-full pointer-events-none web:outline-none ios:leading-[0px] py-0',
    parentVariants: {
        size: { xl: '', lg: '', md: '', sm: '' },
        variant: { underlined: '', outline: '', rounded: '' },
    },
})

const UISelect = createSelect(
    {
        Root: View,
        Trigger: withStyleContext(SelectTriggerWrapper),
        Input: TextInput,
        Icon: UIIcon,
    },
    {
        Portal: Actionsheet,
        Backdrop: ActionsheetBackdrop,
        Content: ActionsheetContent,
        DragIndicator: ActionsheetDragIndicator,
        DragIndicatorWrapper: ActionsheetDragIndicatorWrapper,
        Item: ActionsheetItem,
        ItemText: ActionsheetItemText,
        ScrollView: ActionsheetScrollView,
        VirtualizedList: ActionsheetVirtualizedList,
        FlatList: ActionsheetFlatList,
        SectionList: ActionsheetSectionList,
        SectionHeaderText: ActionsheetSectionHeaderText,
    },
)

cssInterop(UISelect, { className: 'style' })
cssInterop(UISelect.Input, {
    className: { target: 'style', nativeStyleToProp: { textAlign: true } },
})
cssInterop(SelectTriggerWrapper, { className: 'style' })

cssInterop(PrimitiveIcon, {
    className: {
        target: 'style',
        nativeStyleToProp: {
            height: true,
            width: true,
            fill: true,
            color: 'classNameColor',
            stroke: true,
        },
    },
})

type ISelectProps = VariantProps<typeof selectStyle> &
    React.ComponentProps<typeof UISelect> & { className?: string }

const Select = React.forwardRef<React.ComponentRef<typeof UISelect>, ISelectProps>(function Select(
    { className, ...props },
    ref,
) {
    return (
        <UISelect
            className={selectStyle({
                class: className,
            })}
            ref={ref}
            {...props}
        />
    )
})

type ISelectTriggerProps = VariantProps<typeof selectTriggerStyle> &
    React.ComponentProps<typeof UISelect.Trigger> & { className?: string }

const SelectTrigger = React.forwardRef<
    React.ComponentRef<typeof UISelect.Trigger>,
    ISelectTriggerProps
>(function SelectTrigger({ className, size = 'md', variant = 'outline', ...props }, ref) {
    return (
        <UISelect.Trigger
            className={selectTriggerStyle({
                class: className,
                size,
                variant,
            })}
            ref={ref}
            context={{ size, variant }}
            {...props}
        />
    )
})

type ISelectInputProps = VariantProps<typeof selectInputStyle> &
    React.ComponentProps<typeof UISelect.Input> & { className?: string }

const SelectInput = React.forwardRef<React.ComponentRef<typeof UISelect.Input>, ISelectInputProps>(
    function SelectInput({ className, ...props }, ref) {
        const { size: parentSize, variant: parentVariant } = useStyleContext()
        return (
            <UISelect.Input
                className={selectInputStyle({
                    class: className,
                    parentVariants: {
                        size: parentSize,
                        variant: parentVariant,
                    },
                })}
                ref={ref}
                {...props}
            />
        )
    },
)

type ISelectIcon = VariantProps<typeof selectIconStyle> &
    React.ComponentProps<typeof UISelect.Icon> & { className?: string }

const SelectIcon = React.forwardRef<React.ComponentRef<typeof UISelect.Icon>, ISelectIcon>(
    function SelectIcon({ className, size, ...props }, ref) {
        const { size: parentSize } = useStyleContext()
        if (typeof size === 'number') {
            return (
                <UISelect.Icon
                    ref={ref}
                    {...props}
                    className={selectIconStyle({ class: className })}
                    size={size}
                />
            )
        } else if (
            (props?.height !== undefined || props?.width !== undefined) &&
            size === undefined
        ) {
            return (
                <UISelect.Icon
                    ref={ref}
                    {...props}
                    className={selectIconStyle({ class: className })}
                />
            )
        }
        return (
            <UISelect.Icon
                className={selectIconStyle({
                    class: className,
                    size,
                    parentVariants: {
                        size: parentSize,
                    },
                })}
                ref={ref}
                {...props}
            />
        )
    },
)

Select.displayName = 'Select'
SelectTrigger.displayName = 'SelectTrigger'
SelectInput.displayName = 'SelectInput'
SelectIcon.displayName = 'SelectIcon'

// Actionsheet Components
const SelectPortal = UISelect.Portal
const SelectBackdrop = UISelect.Backdrop
const SelectContent = UISelect.Content
const SelectDragIndicator = UISelect.DragIndicator
const SelectDragIndicatorWrapper = UISelect.DragIndicatorWrapper
const SelectItem = UISelect.Item
const SelectScrollView = UISelect.ScrollView
const SelectVirtualizedList = UISelect.VirtualizedList
const SelectFlatList = UISelect.FlatList
const SelectSectionList = UISelect.SectionList
const SelectSectionHeaderText = UISelect.SectionHeaderText

export {
    Select,
    SelectTrigger,
    SelectInput,
    SelectIcon,
    SelectPortal,
    SelectBackdrop,
    SelectContent,
    SelectDragIndicator,
    SelectDragIndicatorWrapper,
    SelectItem,
    SelectScrollView,
    SelectVirtualizedList,
    SelectFlatList,
    SelectSectionList,
    SelectSectionHeaderText,
}
